import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '@wove/web-ui'
import { X, Package } from 'lucide-react'
import { UnitType, CreateUnitTypeRequest, UpdateUnitTypeRequest } from '@wove/api'
import { UnitTypeCategory } from '@wove/common'

const unitTypeSchema = z.object({
  unitTypeCode: z
    .string()
    .min(1, 'Unit type code is required')
    .max(50, 'Unit type code must be 50 characters or less'),
  unitTypeName: z
    .string()
    .min(1, 'Unit type name is required')
    .max(100, 'Unit type name must be 100 characters or less'),
  category: z.string().min(1, 'Category is required'),
  baseUnit: z
    .string()
    .min(1, 'Base unit is required')
    .max(20, 'Base unit must be 20 characters or less'),
  conversionFactor: z.number().min(0.000001, 'Conversion factor must be greater than 0'),
  isActive: z.boolean(),
})

type FormData = z.infer<typeof unitTypeSchema>

interface UnitTypeFormProps {
  unitType?: UnitType
  onSubmit: (data: CreateUnitTypeRequest | UpdateUnitTypeRequest) => void
  onCancel: () => void
  isSubmitting: boolean
}

const UNIT_CATEGORIES = [
  { value: UnitTypeCategory.Weight, label: 'Weight' },
  { value: UnitTypeCategory.Volume, label: 'Volume' },
  { value: UnitTypeCategory.Length, label: 'Length' },
  { value: UnitTypeCategory.PackType, label: 'Pack Type' },
]

export default function UnitTypeForm({
  unitType,
  onSubmit,
  onCancel,
  isSubmitting,
}: UnitTypeFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(unitTypeSchema),
    defaultValues: {
      unitTypeCode: unitType?.unitTypeCode || '',
      unitTypeName: unitType?.unitTypeName || '',
      category: unitType?.category || '',
      baseUnit: unitType?.baseUnit || '',
      conversionFactor: unitType ? parseFloat(unitType.conversionFactor) : 1,
      isActive: unitType?.isActive ?? true,
    },
  })

  const onFormSubmit = (data: FormData) => {
    onSubmit(data)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Package className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {unitType ? 'Edit Unit Type' : 'Create Unit Type'}
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} disabled={isSubmitting}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Unit Type Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit Type Code *
              </label>
              <Controller
                name="unitTypeCode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting}
                    placeholder="e.g., KG, LB, M3"
                    className={errors.unitTypeCode ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.unitTypeCode && (
                <p className="mt-1 text-sm text-red-600">{errors.unitTypeCode.message}</p>
              )}
            </div>

            {/* Unit Type Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit Type Name *
              </label>
              <Controller
                name="unitTypeName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting}
                    placeholder="e.g., Kilogram, Pound, Cubic Meter"
                    className={errors.unitTypeName ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.unitTypeName && (
                <p className="mt-1 text-sm text-red-600">{errors.unitTypeName.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a category</option>
                    {UNIT_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            {/* Base Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Base Unit *</label>
              <Controller
                name="baseUnit"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting}
                    placeholder="e.g., kg, lb, m3"
                    className={errors.baseUnit ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.baseUnit && (
                <p className="mt-1 text-sm text-red-600">{errors.baseUnit.message}</p>
              )}
            </div>

            {/* Conversion Factor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conversion Factor *
              </label>
              <Controller
                name="conversionFactor"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    disabled={isSubmitting}
                    placeholder="1.0"
                    className={errors.conversionFactor ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.conversionFactor && (
                <p className="mt-1 text-sm text-red-600">{errors.conversionFactor.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Factor to convert from this unit to the base unit
              </p>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                )}
              />
              <p className="ml-2 text-xs text-gray-500">Active unit types are available for use</p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : unitType ? 'Update Unit Type' : 'Create Unit Type'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
